import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Fade,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';

import {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../../../../components/V2/Buttons';
import AddPenaltyWaiverModal from '../../../../../../../../../components/V2/PenaltyWaiver/admin/AddPenaltyWaiverModal';
import useTable, {
  defaultRowsPerPageOptions,
  TUseTableState,
} from '../../../../../../../../../components/V2/Table/type1/useTable';
import TableHeadCustom, {
  THeadLabelItem,
} from '../../../../../../../../../components/V2/Table/type1/TableHeadCustom';
import { useAppSelector } from '../../../../../../../../../hooks';
import {
  adminGetPenaltyWaivers,
  getPenaltyWaiverState,
  IPenaltyWaiver,
  resetAdminPenaltyWaiverListData,
  TAdminGetPenaltyWaivers,
} from '../../../../../../../../../store/V2/PenaltyWaiverSlice';
import PenaltyWaiverTableRow from '../../../../../../../../../components/V2/PenaltyWaiver/admin/PenaltyWaiverTableRow';
import TableNoData from '../../../../../../../../../components/V2/Empty/TableNoData';
import TableLoader from '../../../../../../../../../components/Loaders/TableLoader';
import TablePaginationActions from '../../../../../../../../../components/V2/Table/type1/TablePaginationActions';

interface PenaltyWaiversTabProps {
  zoomRequestId: string;
  sectionId: string;
  refreshList: () => void;
}

export type TFilterData = {
  limit: number;
  offset: number;
  sortKey: string;
  sortValue: number;
  zoomRequestId?: string;
};

const defaultListLimit = 30;
const defaultOrderBy = 'createdAt';
const defaultOrder = 'desc';
const defaultTableData: TUseTableState = {
  defaultOrderBy,
  defaultOrder,
  defaultDense: false,
  defaultCurrentPage: 0,
  defaultRowsPerPage: defaultRowsPerPageOptions[0],
  defaultSelected: [],
};

const defaultFilterData: TFilterData = {
  limit: defaultListLimit,
  offset: 0,
  sortKey: defaultOrderBy,
  sortValue: defaultOrder === 'desc' ? -1 : 1,
};

const tableHeaderLabels: THeadLabelItem[] = [
  {
    id: 'createdAt',
    label: 'Created At',
    sortable: true,
  },
  {
    id: 'type',
    label: 'Type',
    sortable: true,
  },
  {
    id: 'amount',
    label: 'Amount',
    sortable: false,
  },
];

const PenaltyWaiversTab: FunctionComponent<PenaltyWaiversTabProps> = ({
  zoomRequestId,
  sectionId,
  refreshList,
}) => {
  const dispatch = useDispatch();

  // Store
  const penaltyWaiversState = useAppSelector(getPenaltyWaiverState);

  // State
  const tableData = useTable(defaultTableData);
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [filterData, setFilterData] = useState({ ...defaultFilterData });

  const { adminPenaltyWaiverListData } = penaltyWaiversState;

  let totalCount = 0;
  let penaltyWaiverList: IPenaltyWaiver[] = [];

  if (adminPenaltyWaiverListData) {
    totalCount = adminPenaltyWaiverListData.totalDocs || 0;

    if (
      adminPenaltyWaiverListData.list &&
      adminPenaltyWaiverListData.list.length
    ) {
      penaltyWaiverList = adminPenaltyWaiverListData.list || [];
    }
  }

  useEffect(() => {
    dispatch(resetAdminPenaltyWaiverListData());

    getPenaltyWaiverListData({ ...filterData });

    return () => {
      dispatch(resetAdminPenaltyWaiverListData());
    };
  }, []);

  const getPenaltyWaiverListData = async (
    filterDataAttributes: TFilterData
  ) => {
    const apiData: TAdminGetPenaltyWaivers = {
      zoomRequestId,
      limit: filterDataAttributes.limit,
      offset: filterDataAttributes.offset,
    };

    if (filterDataAttributes.sortKey) {
      apiData.sortKey = filterDataAttributes.sortKey;
    }

    if (filterDataAttributes.sortValue) {
      apiData.sortValue = filterDataAttributes.sortValue;
    }

    dispatch(adminGetPenaltyWaivers({ ...apiData }));

    setFilterData({ ...filterDataAttributes });
  };

  const handleSort = (id: string) => {
    let newSortValue = 1;

    if (filterData.sortKey === id && filterData.sortValue === 1) {
      newSortValue = -1;
    }

    getPenaltyWaiverListData({
      ...filterData,
      sortValue: newSortValue,
      sortKey: id,
    });

    tableData.onSort(id);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => {
    getPenaltyWaiverListData({
      ...filterData,
      offset: page * filterData.limit,
    });

    tableData.onChangePage(event, page);
    tableData.setSelected([]);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    getPenaltyWaiverListData({
      ...filterData,
      offset: 0,
      limit: parseInt(event.target.value, 10),
    });

    tableData.onChangeRowsPerPage(event);
  };

  return (
    <Fade in>
      <div>
        <div style={{ marginBottom: '16px' }}>
          <Typography fontWeight={600} fontSize="16px">
            Penalty Waivers
          </Typography>
        </div>
        <div>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="flex-end">
                <DarkButton
                  variant="contained"
                  color="inherit"
                  onClick={() => setAddModalOpen(true)}
                  sx={{ ...defaultButtonStyles }}
                >
                  Add
                </DarkButton>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <div>
                <TableContainer
                  sx={{ position: 'relative', maxHeight: '400px' }}
                >
                  <Table
                    stickyHeader
                    size={tableData.dense ? 'small' : 'medium'}
                  >
                    <TableHeadCustom
                      order={tableData.order}
                      orderBy={tableData.orderBy}
                      rowCount={0}
                      headLabels={tableHeaderLabels}
                      numSelected={tableData.selected.length}
                      onSort={handleSort}
                    />
                    <TableBody>
                      {penaltyWaiverList.map((row) => {
                        return (
                          <PenaltyWaiverTableRow
                            key={row._id}
                            penaltyWaiver={row}
                          />
                        );
                      })}
                      {penaltyWaiversState.status !== 'loading' &&
                      !penaltyWaiverList.length ? (
                        <TableNoData title="No penalty waivers found" />
                      ) : null}
                      {penaltyWaiversState.status === 'loading' &&
                      !penaltyWaiverList.length ? (
                        <TableLoader />
                      ) : null}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ position: 'relative' }}>
                  <TablePagination
                    rowsPerPageOptions={defaultRowsPerPageOptions}
                    component="div"
                    count={totalCount}
                    rowsPerPage={tableData.rowsPerPage}
                    page={tableData.page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    ActionsComponent={TablePaginationActions}
                  />

                  <FormControlLabel
                    label="Dense"
                    sx={{
                      px: 3,
                      py: 1.5,
                      top: 0,
                      position: { md: 'absolute' },
                    }}
                    control={
                      <Switch
                        checked={tableData.dense}
                        onChange={tableData.onChangeDense}
                      />
                    }
                  />
                </Box>
              </div>
            </Grid>
          </Grid>
        </div>
        {/* Modals */}
        {isAddModalOpen && zoomRequestId ? (
          <AddPenaltyWaiverModal
            open={isAddModalOpen}
            onClose={() => setAddModalOpen(false)}
            zoomRequestId={zoomRequestId}
            sectionId={sectionId}
            onSubmitted={() => {
              refreshList();

              setAddModalOpen(false);
            }}
          />
        ) : null}
      </div>
    </Fade>
  );
};

export default PenaltyWaiversTab;
